import { intervalToDuration } from "date-fns/esm";

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 60 * SECONDS_IN_MINUTE;
const SECONDS_IN_DAY = 24 * SECONDS_IN_HOUR;

export type DoraTimeUnit = "minutes" | "hours" | "days";

// Decides Y-axis unit so chart ticks stay readable (e.g. "5h" not "300m")
export const getDoraTimeUnit = (averageSeconds: number): DoraTimeUnit => {
  if (averageSeconds >= SECONDS_IN_DAY) return "days";
  if (averageSeconds >= SECONDS_IN_HOUR) return "hours";

  return "minutes";
};

// Converts seconds to a simple number in the display unit (e.g. 7200s → 2 hours) based on the detected unit
export const convertToDisplayValue = (seconds: number, unit: DoraTimeUnit): number => {
  if (unit === "days") return seconds / SECONDS_IN_DAY;
  if (unit === "hours") return seconds / SECONDS_IN_HOUR;

  return seconds / SECONDS_IN_MINUTE;
};

// Reverse of convertToDisplayValue — used for tooltips that need full duration format
export const convertDisplayValueToSeconds = (value: number, unit: DoraTimeUnit): number => {
  if (unit === "days") return value * SECONDS_IN_DAY;
  if (unit === "hours") return value * SECONDS_IN_HOUR;

  return value * SECONDS_IN_MINUTE;
};

export const DORA_AXIS_LABELS: Record<DoraTimeUnit, string> = {
  minutes: "Minutes",
  hours: "Hours",
  days: "Days",
};

// Short Y-axis tick label (e.g. "5h", "2d")
export const formatDoraTickLabel = (value: number, unit: DoraTimeUnit): string => {
  if (unit === "days") return `${value}d`;
  if (unit === "hours") return `${value}h`;

  return `${value}m`;
};

// Detailed duration for metric cards/tooltips (e.g. "2d 5h", "3h 20m")
export const formatDoraDuration = (seconds: number, maxUnit?: DoraTimeUnit): string => {
  if (!seconds) {
    return "0m";
  }

  const { days, hours, minutes } = intervalToDuration({ start: 0, end: seconds * 1000 });

  // When maxUnit is set, stay within that unit (e.g. "69h 0m" instead of "2d 21h")
  if (maxUnit === "minutes") {
    return `${Math.round(seconds / SECONDS_IN_MINUTE)}m`;
  }

  if (maxUnit === "hours") {
    const totalHours = Math.floor(seconds / SECONDS_IN_HOUR);
    const remainingMinutes = Math.floor((seconds % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE);

    return remainingMinutes ? `${totalHours}h ${remainingMinutes}m` : `${totalHours}h`;
  }

  if (days) {
    return hours ? `${days}d ${hours}h` : `${days}d`;
  }

  if (hours) {
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  return `${minutes}m`;
};
